import React, { useState, useEffect, useRef } from "react";
import {
  AppBar, Toolbar, Typography, IconButton, Badge, Avatar, Box,
  Popper, Paper, ClickAwayListener, Grow,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import UserProfile from "../profile/UserProfile";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { logout } from "../../redux/userSlice";
import { bellIconNotificationApi, readNotifcationApi } from "../../api";
import configuration from "../../config/Config";
import { useConfirm } from "../confirmDialog/useConfirm";
import "./Navbar.css";

const SIDEBAR_W = 260;

/* ── theme tokens ── */
const T = {
  green:      "#065F46",
  greenLight: "rgba(6,95,70,0.08)",
  brown:      "#362B24",
  muted:      "#6B7280",
  border:     "#F0F0F0",
  surface:    "#F9FAFB",
  white:      "#ffffff",
  red:        "#EA001B",
  redLight:   "rgba(234,0,27,0.08)",
};

/* ── route labels ── */
const ROUTE_LABELS = {
  dashboard:           "Dashboard",
  "user-management":   "User Management",
  "vender-management": "Vendor Management",
  "all-order":         "All Orders",
  "content-moderation":"Content Moderation",
  "report-analysis":   "Report & Analysis",
  "event-management":  "Event Management",
  "push-notification": "Push Notification",
  "help-support":      "Help & Support",
  "user-permission":   "User Permission",
  "payment-details":   "Payment Transaction",
  blog:                "Blog",
  seo:                 "SEO",
  feedbacks:           "Feedbacks",
};

/* ── small reusable icon button ── */
function NavBtn({ children, badge, dot, onClick, active, sx = {} }) {
  return (
    <IconButton
      onClick={onClick}
      sx={{
        width: 40, height: 40,
        borderRadius: "10px",
        bgcolor: active ? T.greenLight : T.surface,
        border: `1px solid ${T.border}`,
        color: active ? T.green : T.brown,
        transition: "all 0.15s",
        "&:hover": { bgcolor: T.greenLight, color: T.green, borderColor: "transparent" },
        ...sx,
      }}
    >
      {dot !== undefined ? (
        <Badge
          variant="dot"
          invisible={!dot}
          sx={{ "& .MuiBadge-dot": { bgcolor: "#F59E0B", width: 8, height: 8, borderRadius: "50%", top: 2, right: 2 } }}
        >
          {children}
        </Badge>
      ) : children}
    </IconButton>
  );
}

/* ── notification panel ── */
function NotificationPanel({ list, hasMore, loadMore, onClose }) {
  const fmt = (d) => d
    ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <Box sx={{ width: 340, display: "flex", flexDirection: "column" }}>
      {/* header */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: 15, color: T.brown }}>
          Notifications
        </Typography>
        <Box
          onClick={onClose}
          sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.surface, border: `1px solid ${T.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { bgcolor: T.border } }}
        >
          {/* × */}
          <Typography sx={{ fontSize: 16, lineHeight: 1, color: T.muted, mt: "-1px" }}>×</Typography>
        </Box>
      </Box>

      {/* list */}
      <Box id="notif-scroll" sx={{ maxHeight: 320, overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
        <InfiniteScroll
          dataLength={list.length}
          next={loadMore}
          hasMore={hasMore}
          scrollableTarget="notif-scroll"
          loader={
            <Box sx={{ px: 2.5, py: 1 }}>
              <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: T.muted }}>Loading…</Typography>
            </Box>
          }
        >
          {list.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 36, color: T.border, mb: 1 }} />
              <Typography sx={{ fontFamily: "Heebo", fontSize: 13, color: T.muted }}>No notifications yet</Typography>
            </Box>
          ) : list.map((item) => (
            <Box
              key={item._id}
              sx={{ px: 2.5, py: 1.5, display: "flex", gap: 1.5, alignItems: "flex-start", borderBottom: `1px solid ${T.border}`, "&:hover": { bgcolor: T.surface }, transition: "background 0.15s" }}
            >
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: T.greenLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "2px" }}>
                <NotificationsNoneOutlinedIcon sx={{ fontSize: 16, color: T.green }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: "Heebo", fontWeight: 600, fontSize: 12, color: T.brown, mb: 0.25 }}>
                  {item.type || "Notification"}
                </Typography>
                <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: T.muted, lineHeight: 1.4, wordBreak: "break-word" }}>
                  {item.payload?.notification || "—"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: 11, color: T.muted }} />
                  <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: T.muted }}>{fmt(item.createdAt)}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </InfiniteScroll>
      </Box>

      {/* footer */}
      {list.length > 0 && (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}`, textAlign: "center" }}>
          <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: T.muted }}>
            {hasMore ? "Scroll for more" : "All caught up"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ── profile menu ── */
function ProfileMenu({ user, onProfile, onPermission, onLogout, showPermission }) {
  const initials = `${user?.firstName?.charAt(0) || ""}${user?.lastName?.charAt(0) || ""}`.toUpperCase() || "?";

  const Item = ({ icon: Icon, label, onClick, danger }) => (
    <Box
      onClick={onClick}
      sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderRadius: "10px", cursor: "pointer", transition: "all 0.15s",
        color: danger ? T.red : T.brown,
        "&:hover": { bgcolor: danger ? T.redLight : T.surface },
      }}
    >
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: danger ? T.redLight : T.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: 16, color: danger ? T.red : T.muted }} />
      </Box>
      <Typography sx={{ fontFamily: "Heebo", fontSize: 13, fontWeight: 500 }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ width: 260 }}>
      {/* user card */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.border}`, display: "flex", gap: 1.5, alignItems: "center" }}>
        <Avatar
          src={user?.picture ? `${configuration.cdnFront}/${user.picture}` : undefined}
          sx={{ width: 44, height: 44, bgcolor: T.green, fontSize: 16, fontWeight: 600 }}
        >
          {!user?.picture && initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: 14, color: T.brown, lineHeight: 1.3 }} noWrap>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: T.muted, lineHeight: 1.3 }} noWrap>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      {/* menu items */}
      <Box sx={{ p: 1 }}>
        <Item icon={PersonOutlineOutlinedIcon} label="Manage Profile" onClick={onProfile} />
        {showPermission && <Item icon={ManageAccountsOutlinedIcon} label="User Permission" onClick={onPermission} />}
        <Box sx={{ my: 0.5, mx: 1, height: 1, bgcolor: T.border }} />
        <Item icon={LogoutOutlinedIcon} label="Logout" onClick={onLogout} danger />
      </Box>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════ */
const Navbar = ({ OpenSidebar, isMdOrAbove, activeLink }) => {
  const dispatch  = useDispatch();
  const confirm   = useConfirm();
  const token     = useSelector((s) => s?.user?.user?.token);
  const user      = useSelector((s) => s?.user?.user);
  const navigate  = useNavigate();

  const [profileAnchor, setProfileAnchor]   = useState(null);
  const [notifAnchor,   setNotifAnchor]     = useState(null);
  const [modalOpen,     setModalOpen]       = useState(false);
  const [notifList,     setNotifList]       = useState([]);
  const [currentPage,   setCurrentPage]     = useState(1);
  const [totalPages,    setTotalPages]      = useState(1);
  const [hasUnread,     setHasUnread]       = useState(false);

  /* fetch notifications */
  const fetchNotifs = async (page) => {
    try {
      const res = await bellIconNotificationApi({ token, currentPage: page });
      setNotifList((prev) => [...prev, ...res?.notifications]);
      setTotalPages(res?.totalPages);
      setCurrentPage(res?.currentPage);
      setHasUnread(res?.notifications.some((n) => !n.isRead));
    } catch {}
  };

  useEffect(() => { fetchNotifs(currentPage); }, [currentPage]);

  const handleNotifOpen = async (e) => {
    setNotifAnchor(notifAnchor ? null : e.currentTarget);
    if (hasUnread) {
      try { await readNotifcationApi({ token }); setHasUnread(false); } catch {}
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: "Logout",
      description: "Are you sure you want to logout?",
      confirmText: "Logout",
      cancelText: "Cancel",
    });
    if (ok) { dispatch(logout()); toast.success("Logged out"); }
  };

  const pageTitle = ROUTE_LABELS[activeLink] || "Dashboard";
  const initials  = `${user?.firstName?.charAt(0) || ""}`.toUpperCase() || "?";

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          left:    { xs: 0, lg: `${SIDEBAR_W}px` },
          width:   { xs: "100%", lg: `calc(100% - ${SIDEBAR_W}px)` },
          bgcolor: T.white,
          borderBottom: `1px solid ${T.border}`,
          zIndex: 999,
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important", height: 64, px: { xs: 2, sm: 3 }, gap: 1 }}>

          {/* hamburger */}
          {!isMdOrAbove && (
            <IconButton id="navbar-toggle" onClick={() => OpenSidebar()}
              sx={{ color: T.brown, mr: 0.5, flexShrink: 0 }}>
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}

          {/* page title */}
          <Typography sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: { xs: 17, sm: 20 }, color: T.brown, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {pageTitle}
          </Typography>

          {/* right actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>

            {/* notification */}
            <NavBtn dot={hasUnread} active={Boolean(notifAnchor)} onClick={handleNotifOpen}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 19 }} />
            </NavBtn>

            {/* thin divider */}
            <Box sx={{ width: 1, height: 28, bgcolor: T.border, display: { xs: "none", sm: "block" } }} />

            {/* profile trigger */}
            <Box
              onClick={(e) => setProfileAnchor(profileAnchor ? null : e.currentTarget)}
              sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, borderRadius: "10px", cursor: "pointer", border: `1px solid ${profileAnchor ? T.green : "transparent"}`, transition: "all 0.15s", "&:hover": { bgcolor: T.surface } }}
            >
              <Avatar
                src={user?.picture ? `${configuration.cdnFront}/${user.picture}` : undefined}
                sx={{ width: 34, height: 34, bgcolor: T.green, fontSize: 13, fontWeight: 700 }}
              >
                {!user?.picture && initials}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", lineHeight: 1 }}>
                <Typography sx={{ fontFamily: "Heebo", fontWeight: 600, fontSize: 13, color: T.brown, lineHeight: 1.3 }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: T.muted, lineHeight: 1.3 }}>
                  {user?.email}
                </Typography>
              </Box>
              <KeyboardArrowDownOutlinedIcon
                sx={{ fontSize: 16, color: T.muted, display: { xs: "none", md: "block" }, transition: "transform 0.2s", transform: profileAnchor ? "rotate(180deg)" : "none" }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── notification popper ── */}
      <Popper open={Boolean(notifAnchor)} anchorEl={notifAnchor} placement="bottom-end" transition style={{ zIndex: 1300 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top right" }}>
            <Paper sx={{ mt: 1, borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.10)", overflow: "hidden" }}>
              <ClickAwayListener onClickAway={() => setNotifAnchor(null)}>
                <div>
                  <NotificationPanel
                    list={notifList}
                    hasMore={currentPage < totalPages}
                    loadMore={() => setCurrentPage((p) => p + 1)}
                    onClose={() => setNotifAnchor(null)}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* ── profile popper ── */}
      <Popper open={Boolean(profileAnchor)} anchorEl={profileAnchor} placement="bottom-end" transition style={{ zIndex: 1300 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top right" }}>
            <Paper sx={{ mt: 1, borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.10)", overflow: "hidden" }}>
              <ClickAwayListener onClickAway={() => setProfileAnchor(null)}>
                <div>
                  <ProfileMenu
                    user={user}
                    showPermission={user?.role === 3}
                    onProfile={() => { setModalOpen(true); setProfileAnchor(null); }}
                    onPermission={() => { navigate("/user-permission"); setProfileAnchor(null); }}
                    onLogout={handleLogout}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <UserProfile open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default Navbar;
